import { type FormEvent, type ReactElement, useCallback, useEffect, useState } from 'react';
import { type CampaignView, type CampaignState, type CampaignKind, type GrantView } from '@vello/api-client';
import { CAMPAIGN_KILL_REASONS, formatWireMoney, parseMinorAmount, isCurrencyCode, formatMarketInstant, marketDate, marketDayStart } from '../../src/index';
import { DrawerStack } from '../components/drawer-stack';
import { IdRef } from '../components/code-ref';
import { PersonName } from '../components/person-name';
import { useScopeZone } from '../components/scope-zone';
import { useAdmin } from '../session';
import { useConsole } from '../console';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { Badge, Button, Drawer, DrawerBody, DrawerFooter, DrawerHeader, Field, Notice, PageHeader, Panel, Select, Table, TextInput, FormRow, EmptyState, SectionTitle, Grid, PanelHead, Ink, Row, Text, FormStack, EmptyCell, AUTO_REFRESH_MS, useAutoRefresh } from '@vello/web-theme';

/**
 * Campanhas (M14 growth governance, wired; extended in D318) — the operator's governed-campaign desk over the
 * real `/v1/growth/*` surface: the campaign list (`GET /v1/growth/campaigns`), a lookup by id, **draft creation**
 * (`POST /v1/growth/campaigns` — a campaign is born DRAFT and only an audited transition activates it), the
 * lifecycle (activate / pause / end), the instant **kill switch**, **grant issuance** against a campaign and
 * **grant reversal** (fraud/chargeback clawback, budget released). Money is decoded from the wire's minor-unit
 * + ISO-4217 shape. No fabricated rows.
 */
const STATE_TONE: Readonly<Record<CampaignState, 'neutral' | 'success' | 'warning' | 'danger'>> = {
  DRAFT: 'neutral',
  ACTIVE: 'success',
  PAUSED: 'warning',
  ENDED: 'neutral',
};
const KINDS: readonly CampaignKind[] = ['referral', 'first_ride', 'coupon', 'cashback'];


export function CampaignsScreen(): ReactElement {
  const { runtime } = useAdmin();
  // D420 (Admin checklist #2): dates are read and typed on the scope's market wall clock (UTC, marked, when it has none).
  const { zone, lang } = useScopeZone();
  const dayStartIso = (day: string): string => marketDayStart(day, zone) ?? `${day}T00:00:00.000Z`;
  const dayOf = (iso: string): string => formatMarketInstant(iso, zone, lang, 'date');
  const { confirm } = useConsole();
  const { t } = useT();
  const { fail, v } = useVocab();
  const [list, setList] = useState<readonly CampaignView[] | undefined>(undefined);
  const [listError, setListError] = useState<string | undefined>(undefined);
  const [refresh, setRefresh] = useState(0);

  const [id, setId] = useState('');
  const [campaign, setCampaign] = useState<CampaignView | undefined>(undefined);
  const [grants, setGrants] = useState<readonly GrantView[]>([]);
  const [error, setError] = useState<string | undefined>(undefined);
  const [msg, setMsg] = useState<{ tone: 'success' | 'danger'; text: string } | undefined>(undefined);

  const [newId, setNewId] = useState('');
  const [newKind, setNewKind] = useState<CampaignKind>('coupon');
  const [startsAt, setStartsAt] = useState(() => marketDate(new Date(), zone));
  const [endsAt, setEndsAt] = useState('');
  const [createMsg, setCreateMsg] = useState<{ tone: 'success' | 'danger'; text: string } | undefined>(undefined);
  const [creating, setCreating] = useState(false);

  const [grantId, setGrantId] = useState('');
  const [grantUser, setGrantUser] = useState('');
  const [grantAmount, setGrantAmount] = useState('');
  const [grantCurrency, setGrantCurrency] = useState('');
  const [grantExpires, setGrantExpires] = useState('');
  const [reverseReason, setReverseReason] = useState('');

  const loadList = useCallback(async (): Promise<void> => {
    setListError(undefined);
    const res = await runtime.growth.listCampaigns();
    if (res.ok) setList(res.value.campaigns);
    else {
      setList([]);
      setListError(t('camp.error.list', { code: fail(res.error) }));
    }
  }, [runtime, t]);

  useEffect(() => {
    void loadList();
  }, [loadList, refresh]);
  useAutoRefresh(loadList, { intervalMs: AUTO_REFRESH_MS.queue });

  const load = async (campaignId: string): Promise<void> => {
    setError(undefined);
    setMsg(undefined);
    const [c, g] = await Promise.all([runtime.growth.getCampaign(campaignId), runtime.growth.listGrants(campaignId)]);
    if (!c.ok) {
      setCampaign(undefined);
      setGrants([]);
      setError(t('camp.error.load', { code: fail(c.error) }));
      return;
    }
    setCampaign(c.value.campaign);
    setGrants(g.ok ? g.value.grants : []);
  };

  const transition = async (to: CampaignState): Promise<void> => {
    if (campaign === undefined) return;
    const res = await runtime.growth.transitionCampaign(campaign.campaignId, to);
    if (res.ok) {
      setCampaign(res.value.campaign);
      setMsg({ tone: 'success', text: t('camp.transitioned', { state: v('campaign.state', to) }) });
      setRefresh((n) => n + 1);
    } else {
      setMsg({ tone: 'danger', text: t('camp.actionFailed', { code: fail(res.error) }) });
    }
  };

  // D401: the kill carries a reason the operator PICKS. Before this every kill sent the literal
  // `operator kill switch`, so the growth audit could not tell a budget stop from a compliance stop.
  const kill = async (): Promise<void> => {
    if (campaign === undefined) return;
    const answer = await confirm({
      title: t('w6.campaignKill.title', { name: campaign.campaignId }),
      body: t('w6.campaignKill.body'),
      confirmLabel: t('camp.kill'),
      cancelLabel: t('ui.cancel'),
      tone: 'danger',
      icon: 'ban',
      reasons: CAMPAIGN_KILL_REASONS.map((code) => v('campaign.kill_reason', code)),
      reasonLabel: t('w6.reason.label'),
      reasonsOnly: true,
    });
    if (!answer.confirmed || answer.reason === undefined) return;
    const reasonCode = CAMPAIGN_KILL_REASONS.find((code) => v('campaign.kill_reason', code) === answer.reason);
    if (reasonCode === undefined) return;
    const res = await runtime.growth.killCampaign(campaign.campaignId, reasonCode);
    if (res.ok) {
      setCampaign(res.value.campaign);
      setMsg({ tone: 'success', text: t('camp.killed') });
      setRefresh((n) => n + 1);
    } else {
      setMsg({ tone: 'danger', text: t('camp.actionFailed', { code: fail(res.error) }) });
    }
  };

  const createDraft = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (newId.trim() === '' || startsAt === '' || endsAt === '' || startsAt >= endsAt) {
      setCreateMsg({ tone: 'danger', text: t('camp.new.invalid') });
      return;
    }
    setCreating(true);
    setCreateMsg(undefined);
    const res = await runtime.growth.createCampaign({ campaignId: newId.trim(), kind: newKind, startsAt: dayStartIso(startsAt), endsAt: dayStartIso(endsAt) });
    setCreating(false);
    if (res.ok) {
      setCreateMsg({ tone: 'success', text: t('camp.created', { id: res.value.campaign.campaignId }) });
      setNewId('');
      setRefresh((n) => n + 1);
      setId(res.value.campaign.campaignId);
      await load(res.value.campaign.campaignId);
    } else {
      setCreateMsg({ tone: 'danger', text: t('camp.actionFailed', { code: fail(res.error) }) });
    }
  };

  const issue = async (): Promise<void> => {
    if (campaign === undefined) return;
    const minor = parseMinorAmount(grantAmount);
    if (grantId.trim() === '' || grantUser.trim() === '' || minor === undefined || minor === '0' || !isCurrencyCode(grantCurrency) || grantExpires === '') {
      setMsg({ tone: 'danger', text: t('camp.grant.invalid') });
      return;
    }
    const res = await runtime.growth.issueGrant(campaign.campaignId, { grantId: grantId.trim(), userId: grantUser.trim(), amountMinor: minor, currency: grantCurrency.trim(), expiresAt: dayStartIso(grantExpires) });
    if (res.ok) {
      setMsg({ tone: 'success', text: t('camp.grant.issued', { id: res.value.grant.grantId }) });
      setGrantId('');
      setGrantUser('');
      setGrantAmount('');
      await load(campaign.campaignId);
    } else {
      setMsg({ tone: 'danger', text: t('camp.actionFailed', { code: fail(res.error) }) });
    }
  };

  const reverse = async (g: GrantView): Promise<void> => {
    if (campaign === undefined) return;
    const res = await runtime.growth.reverseGrant(g.grantId, reverseReason.trim());
    if (res.ok) {
      setMsg({ tone: 'success', text: t('camp.grant.reversed', { id: g.grantId, amount: formatWireMoney(res.value.releaseAmount) }) });
      await load(campaign.campaignId);
    } else {
      setMsg({ tone: 'danger', text: t('camp.actionFailed', { code: fail(res.error) }) });
    }
  };

  return (
    <>
      <PageHeader title={t('camp.title')} subtitle={t('camp.subtitle')} />

      <Grid columns={2} gap={6}>
        <Panel>
          <SectionTitle>{t('camp.new.title')}</SectionTitle>
          <FormStack gap={4} onSubmit={(e) => void createDraft(e)} noValidate>
            <Grid columns={2} gap={3}>
              <Field label={t('camp.field.id')} htmlFor="camp-new-id">
                <TextInput id="camp-new-id" className="tnum" value={newId} placeholder={t('camp.field.id.placeholder')} onChange={(e) => setNewId(e.target.value)} required />
              </Field>
              <Field label={t('camp.field.kind')} htmlFor="camp-new-kind">
                <Select id="camp-new-kind" value={newKind} onChange={(e) => setNewKind(e.target.value as CampaignKind)}>
                  {KINDS.map((k) => (
                    <option key={k} value={k}>{v('campaign.kind', k)}</option>
                  ))}
                </Select>
              </Field>
              <Field label={t('camp.field.startsAt')} htmlFor="camp-new-start">
                <TextInput id="camp-new-start" className="tnum" type="date" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} required />
              </Field>
              <Field label={t('camp.field.endsAt')} htmlFor="camp-new-end">
                <TextInput id="camp-new-end" className="tnum" type="date" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} required />
              </Field>
            </Grid>
            {createMsg !== undefined && (
              <div>
                <Notice tone={createMsg.tone}>{createMsg.text}</Notice>
              </div>
            )}
            <Button type="submit" variant="primary" disabled={creating}>{t('camp.create')}</Button>
          </FormStack>
        </Panel>

        <Panel>
          <SectionTitle>{t('camp.lookup.title')}</SectionTitle>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void load(id.trim());
            }}
          >
            <FormRow
              action={
                <Button type="submit" variant="secondary" disabled={id.trim() === ''}>{t('camp.lookup')}</Button>
              }
            >
              <Field label={t('camp.field.id')} htmlFor="camp-id">
                <TextInput id="camp-id" className="tnum" value={id} placeholder={t('camp.field.id.placeholder')} onChange={(e) => setId(e.target.value)} />
              </Field>
            </FormRow>
          </form>
          {error !== undefined && <Notice tone="danger">{error}</Notice>}
        </Panel>
      </Grid>

      {listError !== undefined && (
        <div>
          <Notice tone="danger">{listError}</Notice>
        </div>
      )}

      <Panel padded={false}>
        <PanelHead title={t('camp.list.title')} />
        <Table
          columns={[
            { key: 'id', header: t('camp.col.id'), render: (c: CampaignView) => <IdRef value={c.campaignId} /> },
            { key: 'kind', header: t('camp.col.kind'), render: (c: CampaignView) => <Badge tone="neutral">{v('campaign.kind', c.kind)}</Badge> },
            { key: 'state', header: t('camp.col.state'), render: (c: CampaignView) => <Badge tone={c.kill !== undefined ? 'danger' : STATE_TONE[c.state]}>{c.kill !== undefined ? t('camp.state.killed') : v('campaign.state', c.state)}</Badge> },
            { key: 'window', header: t('camp.col.window'), render: (c: CampaignView) => <span className="tnum">{`${dayOf(c.startsAt)} → ${dayOf(c.endsAt)}`}</span> },
            { key: 'version', header: t('camp.col.version'), numeric: true, render: (c: CampaignView) => String(c.version) },
          ]}
          rows={list ?? []}
          rowKey={(c) => c.campaignId}
          onRowClick={(c) => { setId(c.campaignId); void load(c.campaignId); }}
          {...(campaign !== undefined ? { selectedKey: campaign.campaignId } : {})}
          loading={list === undefined}
          loadingLabel={t('ui.loading')}
          empty={<EmptyState title={t('camp.list.empty')} />}
        />
      </Panel>

      <Drawer open={campaign !== undefined} onClose={() => setCampaign(undefined)} labelledBy="camp-detail-title">
        {campaign !== undefined && (
          <>
            <DrawerHeader
              titleId="camp-detail-title"
              title={<IdRef value={campaign.campaignId} copy full />}
              subtitle={
                <>
                  <Badge tone={STATE_TONE[campaign.state]}>{v('campaign.state', campaign.state)}</Badge> <Badge tone="neutral">{v('campaign.kind', campaign.kind)}</Badge>
                </>
              }
              onClose={() => setCampaign(undefined)}
              closeLabel={t('ui.close')}
            />
            <DrawerBody>
              <DrawerStack>
              <Text as="p" role="caption" tone="tertiary" mono>
                {t('camp.window', { from: dayOf(campaign.startsAt), to: dayOf(campaign.endsAt) })} · v{campaign.version}
              </Text>

              {campaign.kill !== undefined && (
                <div>
                  <Notice tone="danger">{t('camp.killedNotice', { reason: campaign.kill.reason, by: campaign.kill.by })}</Notice>
                </div>
              )}
              {msg !== undefined && (
                <div>
                  <Notice tone={msg.tone}>{msg.text}</Notice>
                </div>
              )}

              <SectionTitle level={3} size="sub">{t('camp.grant.issue.title')}</SectionTitle>
              <FormRow
                columns="minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) 110px minmax(0, 1fr)"
                action={
                  <Button variant="secondary" disabled={campaign.state !== 'ACTIVE'} onClick={() => void issue()}>{t('camp.grant.issue')}</Button>
                }
              >
                <Field label={t('camp.field.grantId')} htmlFor="camp-grant-id">
                  <TextInput id="camp-grant-id" className="tnum" value={grantId} onChange={(e) => setGrantId(e.target.value)} />
                </Field>
                <Field label={t('camp.field.userId')} htmlFor="camp-grant-user">
                  <TextInput id="camp-grant-user" className="tnum" value={grantUser} onChange={(e) => setGrantUser(e.target.value)} />
                </Field>
                <Field label={t('camp.field.amount')} htmlFor="camp-grant-amount" hint={t('pay.amount.hint')}>
                  <TextInput id="camp-grant-amount" className="tnum" inputMode="numeric" value={grantAmount} onChange={(e) => setGrantAmount(e.target.value)} />
                </Field>
                <Field label={t('camp.field.currency')} htmlFor="camp-grant-currency" hint="ISO 4217">
                  <TextInput id="camp-grant-currency" value={grantCurrency} maxLength={3} onChange={(e) => setGrantCurrency(e.target.value.toUpperCase())} />
                </Field>
                <Field label={t('camp.field.expiresAt')} htmlFor="camp-grant-expires">
                  <TextInput id="camp-grant-expires" className="tnum" type="date" value={grantExpires} onChange={(e) => setGrantExpires(e.target.value)} />
                </Field>
              </FormRow>

              <Row gap={4} align="end" justify="between">
                <SectionTitle level={3} size="sub">{t('camp.grants')}</SectionTitle>
                <div className="vello-admin-a-field">
                  <Field label={t('camp.field.reverseReason')} htmlFor="camp-reverse-reason">
                    <TextInput id="camp-reverse-reason" value={reverseReason} placeholder={t('ui.reason.placeholder')} onChange={(e) => setReverseReason(e.target.value)} />
                  </Field>
                </div>
              </Row>
              <Table
                columns={[
                  { key: 'grant', header: t('camp.col.grant'), render: (g: GrantView) => <IdRef value={g.grantId} /> },
                  { key: 'user', header: t('camp.col.user'), render: (g: GrantView) => <PersonName id={g.userId} /> },
                  { key: 'amount', header: t('camp.col.amount'), numeric: true, render: (g: GrantView) => formatWireMoney(g.amount) },
                  { key: 'state', header: t('camp.col.grantState'), render: (g: GrantView) => <Badge tone={g.state === 'REVERSED' ? 'danger' : g.state === 'GRANTED' ? 'success' : 'neutral'}>{v('campaign.grant_state', g.state)}</Badge> },
                  { key: 'since', header: t('camp.col.since'), render: (g: GrantView) => <span className="tnum">{dayOf(g.grantedAt)}</span> },
                  {
                    key: 'act',
                    header: '',
                    render: (g: GrantView) => (
                      <Ink as="div" align="end" block>
                        {g.state === 'GRANTED' && (
                          <Button variant="danger" disabled={reverseReason.trim() === ''} onClick={() => void reverse(g)}>{t('camp.grant.reverse')}</Button>
                        )}
                      </Ink>
                    ),
                  },
                ]}
                rows={grants}
                rowKey={(g) => g.grantId}
                empty={<EmptyCell>{t('camp.grants.empty')}</EmptyCell>}
              />
              </DrawerStack>
            </DrawerBody>
            <DrawerFooter>
      {campaign.state !== 'ACTIVE' && campaign.state !== 'ENDED' && <Button variant="secondary" onClick={() => void transition('ACTIVE')}>{t('camp.activate')}</Button>}
      {campaign.state === 'ACTIVE' && <Button variant="secondary" onClick={() => void transition('PAUSED')}>{t('camp.pause')}</Button>}
      {campaign.state !== 'ENDED' && <Button variant="secondary" onClick={() => void transition('ENDED')}>{t('camp.end')}</Button>}
      {campaign.kill === undefined && campaign.state !== 'ENDED' && <Button variant="danger" onClick={() => void kill()}>{t('camp.kill')}</Button>}
            </DrawerFooter>
          </>
        )}
      </Drawer>
    </>
  );
}
